import React, {Fragment, useEffect, useLayoutEffect, useState} from "react";
import {FormContainer} from "../../parts/form-container/FormContainer";
import {Button} from "../../parts/button/Button";
import {createInitialVerificationPageModel, VerificationPageModel, VerificationPageStatus} from "./helpers/verification-page-model";
import {confirmSignUp, resendVerification} from "@simplyhealth/iam_client_lib";
import {
    isCompleted,
    isErrored,
    isMissingQueryParameters,
    isProcessing,
    isRedirect
} from "./helpers/verification-page-status-helper";
import {Progress} from "../../parts/progress/Progress";
import {createInitialFieldModel, FieldModel, isValid} from "../../shared/model/field-model";
import {createRequiredValidator} from "../../helpers/create-validators";
import {createFieldHandlers} from "../../helpers/create-field-handlers";
import {createResendVerificationRequest} from "../../helpers/request-helpers";
import {createResendSuccessMessages} from "../login/helpers/login-response-code-helpers";
import {createUIMessageAndTitleFromResponseCodes} from "./helpers/verification-response-code-helpers";
import {InputWrapper} from "../../parts/input-wrapper/InputWrapper";
import {fromQueryParameters} from "../../helpers/query-parameters-helper";
import {updatePageModelForStatusChangeReducer, updatePageModelReducer} from "./helpers/verification-page-model-reducer";
import {createConfirmSignUpRequest} from "./helpers/verification-request-helper";
import {ANALYTICS_NAMES, sendPageVisitAnalytics} from "../../helpers/analytics-helpers";
import {CodeDeliveryDto} from "../../shared/model/user-model";
import {useHistory, useLocation} from "react-router-dom";
import {ENTER_IT_BELOW_TO_CONFIRM, sendCodeToEmailToConfirm} from "../../helpers/constants";
import {ClickLink} from "../../parts/click-link/ClickLink";
import styles from "./Verification.module.scss";

export const Verification = () => {
    const PAGE_NAME = "verification";

    const locationValues = useLocation();
    const history = useHistory();

    const [pageStatus, setPageStatus] = useState<VerificationPageStatus>("Sending");
    const [codeDeliveryDto, setCodeDeliveryDto] = useState<CodeDeliveryDto>();

    const [pageModel, setPageModel] = useState<VerificationPageModel>(createInitialVerificationPageModel());

    const [codeModel, setCodeModel] = useState<FieldModel>(createInitialFieldModel());
    const [onCodeChange, onCodeBlur, invokeCodeValidation] = createFieldHandlers(
        codeModel,
        setCodeModel,
        createRequiredValidator("code")
    );

    useLayoutEffect(() => {
        if (!fromQueryParameters().doAllStandardExist()) {
            setPageStatus(() => "Missing Query Parameters");
        }
        if (codeDeliveryDto) {
            setPageStatus("Ready");
            handlePageStatusChange(codeDeliveryDto);
        } else {
            if (locationValues.state) {
                setCodeDeliveryDto(locationValues.state as CodeDeliveryDto);
                setPageStatus("Ready");
                handlePageStatusChange(locationValues.state as CodeDeliveryDto);
            } else {
                const [informationMessages, title] = createUIMessageAndTitleFromResponseCodes("", "");
                setPageModel(updatePageModelReducer(pageModel, {informationMessages, title}));
                setPageStatus("Missing Query Parameters");
            }
        }
    }, []);

    useEffect(() => {
        sendPageVisitAnalytics(ANALYTICS_NAMES.verification);
    }, [])

    useEffect(() => {
        handlePageStatusChange(codeDeliveryDto);
    }, [pageStatus]);

    const onConfitmCodeSingUpClick = () => {
        invokeCodeValidation();
        if (isValid(codeModel)) {
            setPageStatus(() => "Checking Account Status");
            if (!codeDeliveryDto) {
                setPageStatus(() => "Missing Query Parameters");
                return;
            }
            const request = createConfirmSignUpRequest(
                codeDeliveryDto.realCodeDeliveryDestination,
                codeModel.value
            );
            // console.log(request);
            confirmSignUp(request)
                .then(response => {
                    // console.log(response);
                    if (response.status === "succeeded") {
                        setPageStatus(() => "Succeeded");
                    } else {
                        errorReqHandler(response?.code, response?.message);
                    }
                })
                .catch(error => {
                    errorReqHandler(error?.code, error?.message);
                });
        }
    };

    const handlePageStatusChange = (resetDto?: CodeDeliveryDto) => {
        let informationMessages: string[] = pageModel.informationMessages;
        if (pageStatus === "Ready" && resetDto) {
            informationMessages = [sendCodeToEmailToConfirm(resetDto?.codeDeliveryDestination), ENTER_IT_BELOW_TO_CONFIRM];
        }
        setPageModel(pm => updatePageModelForStatusChangeReducer(updatePageModelReducer(pm, {informationMessages}), pageStatus));
    };

    const errorReqHandler = (errorCode: string, errorMessage?: string) => {
        let [informationMessages, title] = createUIMessageAndTitleFromResponseCodes(errorCode, errorMessage);
        if (errorMessage) {
            informationMessages = [errorMessage];
        }
        setPageModel(updatePageModelReducer(pageModel, {informationMessages, title}));
        setPageStatus("Failed");
        sendPageVisitAnalytics(ANALYTICS_NAMES.verificationError);
    };

    const onResendVerficationSubmitClick = () => {
        switch (pageStatus) {
            case "Ready":
            case "Resend Verification Succeeded":
                sendResendVerficationRequest();
                break;
        }
    };

    const onSubmitClick = () => {
        switch (pageStatus) {
            case "Ready":
            case "Resend Verification Succeeded":
                onConfitmCodeSingUpClick();
                break;
            case "Sending":
                // Do nothing
                break;
            case "Succeeded":
                history.push("/");
                break;
            case "Failed":
                setPageStatus("Ready");
                break;
        }
    };

    const sendResendVerficationRequest = () => {
        if (codeDeliveryDto) {
            setPageStatus("Sending");
            const request = createResendVerificationRequest(codeDeliveryDto.realCodeDeliveryDestination);
            resendVerification(request)
                .then(response => {
                    if (response.status === "succeeded") {
                        const informationMessages = createResendSuccessMessages(response.codeDeliveryDestination);
                        fromQueryParameters().clearVerificationParamters();
                        setPageModel(updatePageModelReducer(pageModel, {informationMessages}));
                        setPageStatus("Resend Verification Succeeded");
                    } else {
                        const [informationMessages, title] = createUIMessageAndTitleFromResponseCodes(
                            response.code,
                            response.supportCode
                        );
                        setPageModel(updatePageModelReducer(pageModel, {informationMessages, title}));
                        setPageStatus("Failed");
                    }
                })
                .catch(error => {
                    const [informationMessages, title] = createUIMessageAndTitleFromResponseCodes("", "");
                    setPageModel(updatePageModelReducer(pageModel, {informationMessages, title}));
                    setPageStatus("Failed");
                });
        } else {
            errorReqHandler("", "");
        }
    };

    return (
        <FormContainer
            pageName={PAGE_NAME}
            title={pageModel.title}
            messages={pageModel.informationMessages}
            image={<img src={pageModel.titleImage} alt=""/>}
            fields={
                isProcessing(pageStatus) || isRedirect(pageStatus)? (
                    <Fragment>
                        <Progress/>
                    </Fragment>
                ) : isCompleted(pageStatus) || isErrored(pageStatus) || isCompleted(pageStatus) || isMissingQueryParameters(pageStatus) ? (
                    <Fragment/>
                ) : (
                    <Fragment>
                        <InputWrapper
                            pageName={PAGE_NAME}
                            name="code"
                            inputType="text"
                            autoCompleteType=""
                            caption="Code"
                            fieldState={codeModel}
                            shouldDisplayRequired
                            onInputChange={onCodeChange}
                            onInputBlur={onCodeBlur}
                        />
                        <div className={styles.verifiaction_container}>
                            <ClickLink pageName={PAGE_NAME} name="resend_code" caption={"Resend the code"} onClick={onResendVerficationSubmitClick}/>
                        </div>
                    </Fragment>
                )
            }
            action={
                pageModel.showButton && (
                    <Button pageName={PAGE_NAME} name="submit" caption={pageModel.buttonTitle} onClick={onSubmitClick}/>
                )
            }
        />
    );
};
